import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchAllSharedCalendars,
  fetchSharedCalendars,
  type SharedCalendar,
} from './sharedCalendarsApi';

export function buildSharedCalendarsQueryKey(type: string) {
  return ['shared-calendars', type] as const;
}

/**
 * Never cached. Shared calendars are an ACCOUNT capability an admin can change
 * at any moment — turning the feature off, adding one, revoking access — and
 * every surface that reads them decides what to render from the answer. Holding
 * them for 2 minutes (and 5 in cache) meant closing a drawer, changing the
 * setting, and reopening replayed the old list (CORE-5158).
 *
 * `gcTime: 0` matters as much as `staleTime`: leaving data behind lets a remount
 * paint the stale list first — with `isLoading` false — while the re-read is
 * still in flight.
 */
const NEVER_CACHED = { staleTime: 0, gcTime: 0 } as const;

export function useSharedCalendarsQuery({
  type = 'saved',
  enabled = true,
}: {
  type?: string;
  enabled?: boolean;
} = {}): UseQueryResult<SharedCalendar[], Error> {
  return useQuery({
    queryKey: buildSharedCalendarsQueryKey(type),
    queryFn: ({ signal }) => fetchSharedCalendars(type, signal),
    enabled,
    ...NEVER_CACHED,
    retry: false,
  });
}

/**
 * Plan-authoring fetch — `GET /shared_calendars/` (index). Returns
 * every saved shared calendar in the workspace without filtering by
 * the caller's access. See `fetchAllSharedCalendars` for the why.
 *
 * Keyed under `['shared-calendars', 'all']` so it doesn't share cache
 * with the access-filtered `useSharedCalendarsQuery({type: 'saved'})`.
 */
export function useAllSharedCalendarsQuery({
  enabled = true,
}: {
  enabled?: boolean;
} = {}): UseQueryResult<SharedCalendar[], Error> {
  return useQuery({
    queryKey: buildSharedCalendarsQueryKey('all'),
    queryFn: ({ signal }) => fetchAllSharedCalendars(signal),
    enabled,
    ...NEVER_CACHED,
    retry: false,
  });
}
